import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const testimonials = [
  {
    name: "María González",
    role: "Terapeuta Certificada en Biomagnetismo",
    city: "Guadalajara, Jalisco",
    stars: 5,
    text: "El Instituto Ashaika cambió mi vida por completo. Llegué buscando sanar mi cuerpo y encontré una vocación. Hoy tengo mi propio consultorio y ayudo a decenas de personas cada mes. La formación es de un nivel que no encontrarás en ningún otro lugar.",
    img: "https://public.readdy.ai/ai/img_res/7b61c61f781dbab2f32b9ca593c003f1.jpg",
  },
  {
    name: "Carlos Mendoza",
    role: "Alumno de Método Yuen e Hipnosis",
    city: "Ciudad de México",
    stars: 5,
    text: "Llevaba años con ansiedad crónica y ningún tratamiento convencional me ayudaba. Después del diplomado de Hipnosis Terapéutica, no solo me sané a mí mismo, sino que ahora ayudo a mis pacientes a liberarse de sus bloqueos. Alonso es un maestro excepcional.",
    img: "https://readdy.ai/api/search-image?query=professional%20mexican%20man%20therapist%20healer%20smiling%20confident%20portrait%20warm%20lighting%20natural%20background%2040%20years%20old&width=300&height=300&seq=test002&orientation=squarish",
  },
  {
    name: "Laura Ramírez",
    role: "Especialista en Bioenergética",
    city: "Monterrey, Nuevo León",
    stars: 5,
    text: "La metodología del Instituto Ashaika es única. Integran ciencia y energía de una manera que tiene sentido total. Después del taller de Bioenergética, mi vida cambió en todos los niveles: salud, relaciones, abundancia. Es una inversión que se paga sola.",
    img: "https://readdy.ai/api/search-image?query=professional%20mexican%20woman%20healer%20therapist%20smiling%20confident%20portrait%20warm%20golden%20lighting%20natural%20background%2032%20years%20old&width=300&height=300&seq=test003&orientation=squarish",
  },
  {
    name: "Roberto Flores",
    role: "Médico y Terapeuta Alternativo",
    city: "Zapopan, Jalisco",
    stars: 5,
    text: "Como médico convencional, llegué con escepticismo. Me fui con una visión completamente nueva de la salud. El Biomagnetismo y las Leyes Biológicas complementan perfectamente mi práctica médica. Mis pacientes obtienen resultados que antes eran imposibles.",
    img: "https://readdy.ai/api/search-image?query=professional%20mexican%20doctor%20man%20smiling%20confident%20portrait%20white%20coat%20warm%20lighting%20natural%20background%2045%20years%20old&width=300&height=300&seq=test004&orientation=squarish",
  },
  {
    name: "Ana Sofía Torres",
    role: "Coach y Terapeuta Holística",
    city: "Guadalajara, Jalisco",
    stars: 5,
    text: "El diplomado de Desarrollo Personal y Conciencia fue la experiencia más transformadora de mi vida. Alonso tiene un don especial para transmitir conocimiento profundo de manera accesible. Hoy soy una persona completamente diferente, más libre y más poderosa.",
    img: "https://readdy.ai/api/search-image?query=professional%20mexican%20woman%20coach%20therapist%20smiling%20confident%20portrait%20warm%20lighting%20natural%20background%2028%20years%20old&width=300&height=300&seq=test005&orientation=squarish",
  },
];

export default function Testimonials() {
  const { ref, inView } = useInView();
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c === 0 ? testimonials.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === testimonials.length - 1 ? 0 : c + 1));

  const t = testimonials[current];

  return (
    <section id="testimonios" className="py-20 md:py-32 bg-[#0A1F44]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Testimonios</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Transformaciones que hablan<br />
            <span className="text-[#D4AF37]">por sí solas</span>
          </h2>
        </div>

        {/* Featured Testimonial */}
        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center mb-12 transition-all duration-700 delay-100 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Text */}
          <div className="lg:col-span-3">
            <div className="text-[#D4AF37] text-8xl leading-none font-serif mb-2 opacity-60">&ldquo;</div>
            <p className="text-white text-lg md:text-xl leading-relaxed mb-6 -mt-4">
              {t.text}
            </p>
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: t.stars }).map((_, i) => (
                <i key={i} className="ri-star-fill text-[#D4AF37] text-lg"></i>
              ))}
            </div>
            <div>
              <div className="text-white font-bold text-lg">{t.name}</div>
              <div className="text-[#D4AF37] text-sm">{t.role}</div>
              <div className="text-white/50 text-sm flex items-center gap-1 mt-1">
                <i className="ri-map-pin-line text-xs"></i>
                {t.city}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-4 mt-8">
              <button
                onClick={prev}
                className="w-11 h-11 flex items-center justify-center rounded-full border border-white/30 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <button
                onClick={next}
                className="w-11 h-11 flex items-center justify-center rounded-full bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a42e] transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
              <span className="text-white/40 text-sm">{current + 1} / {testimonials.length}</span>
            </div>
          </div>

          {/* Photo */}
          <div className="lg:col-span-2 flex justify-center lg:justify-end">
            <div className="relative">
              <div className="w-64 h-72 md:w-72 md:h-80 rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30">
                <img
                  src={t.img}
                  alt={t.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#D4AF37] text-[#0A1F44] rounded-xl px-4 py-2 text-sm font-bold">
                ⭐ Alumno Certificado
              </div>
            </div>
          </div>
        </div>

        {/* All testimonials mini cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {testimonials.map((t2, i) => (
            <button
              key={t2.name}
              onClick={() => setCurrent(i)}
              className={`p-3 rounded-xl border text-left transition-all duration-300 cursor-pointer ${
                current === i
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : "border-white/10 bg-white/5 hover:border-white/30"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={t2.img} alt={t2.name} className="w-full h-full object-cover" />
                </div>
                <div className="text-white text-xs font-semibold truncate">{t2.name.split(" ")[0]}</div>
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#D4AF37] text-xs"></i>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
